<template>
  <div
    class="flex flex-row text-left items-center gap-2 p-2 bg-n-10 dark:bg-n-700 text-xs h-12 max-h-12"
    :class="[isSortable && column.sortable && !disabled ? 'cursor-pointer' : 'cursor-default']"
    @click="onClick"
  >
    <div v-if="column.checkbox" data-testid="l-table-header-cell__checkbox">
      <l-checkbox v-model="checkboxState" small @click="emit('checkbox', checkboxState, column)" />
    </div>
    <div v-if="column.radio" data-testid="l-table-header-cell__radio">
      <l-radio v-model="radioState" small @toggle="emit('radio', radioState, column)" />
    </div>
    <div v-if="column.avatar?.text" data-testid="l-table-header-cell__avatar">
      <l-avatar
        :style="{ backgroundColor: avatarColor, color: avatarTextColor }"
        size="small"
        :value="column.avatar?.text"
      />
    </div>
    <div v-if="spotColor" data-testid="l-table-header-cell__spot">
      <div class="w-2 h-2 rounded-full" :style="{ backgroundColor: spotColor }" />
    </div>
    <div
      class="line-clamp-2"
      :class="{
        'text-v-500': !!sortDirection
      }"
      data-testid="l-table-header-cell__text"
    >
      <slot>{{ column.header }}</slot>
    </div>
    <div v-if="column.iconRight" data-testid="l-table-header-cell__icon">
      <l-icon class="align-center" size="small" :name="column.iconRight" />
    </div>
    <div v-if="isSortable && column.sortable" data-testid="l-table-header-cell__sortable">
      <l-icon
        v-if="sortDirection && ['ASC', 'DESC'].includes(sortDirection)"
        class="align-center text-v-500"
        size="small"
        :name="sortDirection === 'DESC' ? 'small-bracket-down' : 'small-bracket-up'"
      />
      <l-icon v-else class="align-center text-v-500" size="small" name="sorting" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import LAvatar from '../LAvatar.vue'
import LCheckbox from '../LCheckbox.vue'
import LIcon from '../LIcon.vue'
import LRadio from '../LRadio.vue'
import type { TableDataColumn, TableSortDirection } from './types'

type Props = {
  /** Data used to build the different variations for the column */
  column: TableDataColumn
  /** Defines from the outside if the checkbox for the column is selected */
  checkboxSelected?: boolean
  /** Defines from the outside if the radio for the column is selected */
  radioSelected?: boolean
  /** Defines from the outside if the column can show the sortable state */
  isSortable?: boolean
  /** Defines the state for the sorting icon */
  sortDirection?: TableSortDirection
  /** Indicates whether the functionality or component is disabled */
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isSortable: false,
  checkboxSelected: false,
  radioSelected: false,
  sortDirection: undefined,
  disabled: false
})

const emit = defineEmits<{
  /** Emitted when the whole headerCell is clicked */
  click: [event: MouseEvent]
  /** Emitted when the checkbox state of the column changes */
  checkbox: [value: boolean, item: TableDataColumn]
  /** Emitted when the radio state of the column changes */
  radio: [value: boolean, item: TableDataColumn]
  /** Emitted when the headerCell is clicked and isSortable */
  sort: [key: string, direction: TableSortDirection]
}>()

defineSlots<{
  /** This is the default slot, used to override the text part of the component */
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  default: {}
}>()

const spotColor = ref<string>('')
const avatarColor = ref<string>('')
const avatarTextColor = ref<string>('')

const checkboxState = ref<boolean>(false)
const radioState = ref<boolean>(false)

watch(
  () => props.column,
  value => {
    if (value.spot && value.spot !== '') {
      spotColor.value = parseHexColor(value.spot)
    } else {
      spotColor.value = ''
    }

    if (value.avatar?.text) {
      avatarColor.value = parseHexColor(value.avatar.color || '', '#2A2A42')
      avatarTextColor.value = value.avatar.textColor
        ? parseHexColor(value.avatar.textColor || '', '#ffffff')
        : '#ffffff'
    }
  },
  { immediate: true }
)

watch(
  () => props.radioSelected,
  value => {
    radioState.value = value
  }
)

watch(
  () => props.checkboxSelected,
  value => {
    checkboxState.value = value
  }
)

function onClick(event: MouseEvent): void {
  if (props.disabled) return

  if (props.isSortable && props.column.sortable) {
    onSortingClick()
  }

  emit('click', event)
}

function onSortingClick(): void {
  if (!props.isSortable || !props.column.sortable) {
    return
  }

  let newDirection: TableSortDirection
  if (props.sortDirection && props.sortDirection === 'DESC') {
    newDirection = 'ASC'
  } else if (props.sortDirection && props.sortDirection === 'ASC') {
    newDirection = undefined
  } else {
    newDirection = 'DESC'
  }

  emit('sort', props.column.key, newDirection)
}

function parseHexColor(color: string, fallback = '#9C6DFE'): string {
  if (color !== '') {
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
    if (hex.test(color)) {
      return color
    }
  }

  return fallback
}
</script>
